import React from 'react';
import { ApiClientError } from '@shared/api/api-client';
import { sidebarPreferencesApi } from '@shared/api/sidebar-preferences';
import { NAV_GROUP_ORDER, type NavGroupId, type SidebarPreferences } from '@shared/lib/navigation';

/** Local copy of the preferences: `version` is the server version they were based on. */
type SyncState = SidebarPreferences & {
  version: number;
  pending: boolean;
  loaded: boolean;
  busy: boolean;
};

const SAVE_DEBOUNCE_MS = 350;

const normalizePreferences = (value: unknown): SidebarPreferences => {
  const input = value && typeof value === 'object' ? (value as Partial<SidebarPreferences>) : {};
  const savedGroups = Array.isArray(input.groupOrder)
    ? input.groupOrder.filter((group): group is NavGroupId => NAV_GROUP_ORDER.includes(group))
    : [];
  return {
    groupOrder: [...new Set([...savedGroups, ...NAV_GROUP_ORDER])],
    favorites: Array.isArray(input.favorites)
      ? [
          ...new Set(
            input.favorites.filter(
              (key): key is string => typeof key === 'string' && /^[a-z0-9-]{1,64}$/.test(key)
            )
          ),
        ].slice(0, 30)
      : [],
  };
};

const readCache = (storageKey: string): SyncState => {
  let raw: { version?: unknown; pending?: unknown } | null = null;
  try {
    raw = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
  } catch {
    /* Storage can be unavailable or hold garbage. */
  }
  return {
    ...normalizePreferences(raw),
    version: Number.isInteger(raw?.version) ? (raw?.version as number) : 0,
    pending: raw?.pending === true,
    loaded: false,
    busy: false,
  };
};

/**
 * Staff sidebar preferences. The server is the source of truth; localStorage only
 * paints the first frame and holds edits that have not been saved yet.
 */
export const useSidebarPreferences = (enabled: boolean, userId: string, role: string) => {
  const storageKey = `jema-sidebar-preferences:${userId}:${role}`;
  const state = React.useRef<SyncState>(null as unknown as SyncState);
  state.current ??= readCache(storageKey);
  const [preferences, setPreferences] = React.useState<SidebarPreferences>(state.current);
  const [syncError, setSyncError] = React.useState(false);
  const timer = React.useRef<number | undefined>(undefined);

  const commit = React.useCallback(
    (s: SyncState) => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            groupOrder: s.groupOrder,
            favorites: s.favorites,
            version: s.version,
            pending: s.pending,
          })
        );
      } catch {
        /* Storage can be unavailable. */
      }
      setPreferences({ groupOrder: s.groupOrder, favorites: s.favorites });
    },
    [storageKey]
  );

  // Loads from the server, then pushes pending edits. A replaced `state.current`
  // (account or role switch) abandons the run.
  const sync = React.useCallback(async () => {
    const s = state.current;
    if (s.busy) return;
    s.busy = true;
    try {
      for (;;) {
        if (!s.loaded) {
          const remote = await sidebarPreferencesApi.get(role);
          if (state.current !== s) return;
          const remoteVersion = Number(remote?.version) || 0;
          // Pending edits survive only if the server still holds the version they
          // were made on; otherwise the newer server data wins.
          if (!s.pending || s.version !== remoteVersion) {
            Object.assign(s, normalizePreferences(remote), {
              version: remoteVersion,
              pending: false,
            });
            commit(s);
          }
          s.loaded = true;
        }
        if (!s.pending) break;
        const { groupOrder, favorites } = s;
        try {
          const saved = await sidebarPreferencesApi.save(role, {
            groupOrder,
            favorites,
            version: s.version,
          });
          if (state.current !== s) return;
          s.version = saved.version;
          // Edits made while the save was in flight keep it pending for another pass.
          if (s.groupOrder === groupOrder && s.favorites === favorites) s.pending = false;
          commit(s);
        } catch (error) {
          if (!(error instanceof ApiClientError && error.status === 409)) throw error;
          // ponytail: on conflict the server copy wins whole; merge per edit if
          // people start losing offline changes.
          s.loaded = false;
        }
      }
      setSyncError(false);
    } catch {
      if (state.current === s) setSyncError(true);
    } finally {
      s.busy = false;
    }
  }, [commit, role]);

  React.useEffect(() => {
    if (!enabled) return;
    const s = readCache(storageKey);
    state.current = s;
    setPreferences({ groupOrder: s.groupOrder, favorites: s.favorites });
    setSyncError(false);
    void sync();
    const retry = () => void sync();
    window.addEventListener('online', retry);
    return () => {
      window.removeEventListener('online', retry);
      window.clearTimeout(timer.current);
    };
  }, [enabled, storageKey, sync]);

  const edit = React.useCallback(
    (change: (current: SidebarPreferences) => Partial<SidebarPreferences>) => {
      const s = state.current;
      Object.assign(s, change(s), { pending: true });
      commit(s);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void sync(), SAVE_DEBOUNCE_MS);
    },
    [commit, sync]
  );

  return { preferences, edit, syncError, retry: sync };
};
